import React, { useState, useRef } from 'react';
import { Upload, X, CheckCircle, AlertCircle, Play, Pause } from 'lucide-react';
import { useChunkedUpload } from '../../hooks/useChunkedUpload';
import unifiedAuth from '../../utils/unifiedAuth';
import VideoUploadTips from './VideoUploadTips';

interface SmartFileUploadProps {
  onUploadComplete: (result: any) => void;
  onUploadError: (error: string) => void;
  acceptedTypes?: string[];
  maxFileSize?: number;
  chunkThreshold?: number; // Files larger than this will use chunked upload
  className?: string;
  disabled?: boolean;
}

const SmartFileUpload: React.FC<SmartFileUploadProps> = ({
  onUploadComplete,
  onUploadError,
  acceptedTypes = ['video/*', 'audio/*', 'image/*'],
  maxFileSize = 25 * 1024 * 1024 * 1024, // 25GB for 4K videos
  chunkThreshold = 10 * 1024 * 1024, // 10MB - matches backend limit, use chunked upload for larger files
  className = '',
  disabled = false
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Chunked upload hook
  const {
    uploadState,
    startUpload,
    pauseUpload,
    resumeUpload,
    cancelUpload,
  } = useChunkedUpload({
    onUploadComplete: (result) => {
      onUploadComplete({ ...result, durationSeconds: extractedDurationRef.current });
      extractedDurationRef.current = null;
      setSelectedFile(null);
    },
    onUploadError: (error) => {
      extractedDurationRef.current = null;
      onUploadError(error);
    },
    maxFileSize,
  });

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatTime = (seconds: number): string => {
    if (!isFinite(seconds) || seconds < 0) return '--:--';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const validateFile = (file: File): string | null => {
    // Check file size
    if (file.size > maxFileSize) {
      return `File size (${formatBytes(file.size)}) exceeds maximum allowed size (${formatBytes(maxFileSize)})`;
    }

    // Check file type
    const isValidType = acceptedTypes.some(type => {
      if (type.endsWith('/*')) {
        return file.type.startsWith(type.slice(0, -1));
      }
      return file.type === type;
    });

    if (!isValidType) {
      return `File type (${file.type}) is not supported. Accepted types: ${acceptedTypes.join(', ')}`;
    }

    return null;
  };

  const getLargeFileWarning = (file: File): string | null => {
    const sizeGB = file.size / (1024 * 1024 * 1024);
    
    if (sizeGB > 15) {
      return `⚠️ Very large file (${formatBytes(file.size)}). This may take 30+ minutes to upload depending on your connection.`;
    } else if (sizeGB > 5) {
      return `⚠️ Large file (${formatBytes(file.size)}). Upload may take 10-30 minutes.`;
    } else if (sizeGB > 1) {
      return `📹 4K/HD video detected (${formatBytes(file.size)}). Upload may take 5-15 minutes.`;
    }
    
    return null;
  };

  const handleFileSelect = async (file: File) => {
    if (disabled) return;

    // Check authentication first
    if (!unifiedAuth.isAuthenticated()) {
      onUploadError('Please log in to upload files. You need to be authenticated to upload media.');
      return;
    }

    const validationError = validateFile(file);
    if (validationError) {
      onUploadError(validationError);
      return;
    }

    setSelectedFile(file);

    // Extract duration for video/audio files before starting upload
    if (file.type.startsWith('video/') || file.type.startsWith('audio/')) {
      await extractMediaDuration(file);
    }

    // Determine upload method based on file size
    if (file.size > chunkThreshold) {
      // Use chunked upload for large files
      startUpload(file);
    } else {
      // Use regular upload for smaller files
      uploadRegularFile(file);
    }
  };

  // Track extracted duration to include in upload result
  const extractedDurationRef = useRef<number | null>(null);

  const extractMediaDuration = (file: File): Promise<void> => {
    return new Promise((resolve) => {
      const url = URL.createObjectURL(file);
      const media = document.createElement(file.type.startsWith('video/') ? 'video' : 'audio');
      media.preload = 'metadata';
      media.onloadedmetadata = () => {
        if (isFinite(media.duration) && media.duration > 0) {
          extractedDurationRef.current = Math.round(media.duration);
        }
        URL.revokeObjectURL(url);
        resolve();
      };
      media.onerror = () => {
        URL.revokeObjectURL(url);
        resolve();
      };
      media.src = url;
    });
  };

  const uploadRegularFile = async (file: File) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await unifiedAuth.fetch('/api/media/upload', {
        method: 'POST',
        // No headers: the token is attached by unifiedAuth.fetch, and Content-Type
        // must be left to the browser so it sets the multipart boundary.
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
      }

      const result = await response.json();
      const data = result.data || result;
      onUploadComplete({ ...data, durationSeconds: extractedDurationRef.current });
      extractedDurationRef.current = null;
      setSelectedFile(null);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Upload failed';
      extractedDurationRef.current = null;
      onUploadError(errorMessage);
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (!disabled) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);
    
    if (disabled) return;

    const file = event.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const { status, progress, error, isPaused } = uploadState;
  const isUploading = status === 'uploading';
  const isCompleted = status === 'completed';
  const hasError = status === 'error';

  return (
    <div className={`w-full ${className}`}>
      {/* File Selection Area */}
      {status === 'idle' && (
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer
            ${isDragOver && !disabled ? 'border-blue-400 bg-blue-50' : 'border-gray-300'}
            ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-blue-400 hover:bg-blue-50'}
          `}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => !disabled && fileInputRef.current?.click()}
        >
          <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-lg font-medium text-gray-700 mb-2">
            Choose a file to upload or drag and drop
          </p>
          <p className="text-sm text-gray-500 mb-4">
            Maximum file size: {formatBytes(maxFileSize)}
          </p>
          {!unifiedAuth.isAuthenticated() && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
              <p className="text-sm text-yellow-800">
                ⚠️ You must be logged in to upload files. Please log in first.
              </p>
            </div>
          )}
          <p className="text-xs text-gray-400">
            Files larger than {formatBytes(chunkThreshold)} will use chunked upload for better reliability
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptedTypes.join(',')}
            onChange={handleInputChange}
            className="hidden"
            disabled={disabled}
          />
        </div>
      )}

      {/* Video Upload Tips */}
      {selectedFile && selectedFile.size > 1024 * 1024 * 1024 && ( // Show for files > 1GB
        <VideoUploadTips 
          fileSize={selectedFile.size} 
          className="mb-4"
        />
      )}

      {/* Upload Progress */}
      {(isUploading || isCompleted) && selectedFile && (
        <div className="space-y-4 p-6 bg-white rounded-lg border">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-medium text-gray-900 truncate">
                {selectedFile.name}
              </h3>
              {progress.percentage === 0 && isUploading && (
                <div className="flex items-center space-x-2 mt-1">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  <span className="text-sm text-blue-600">Initializing upload...</span>
                </div>
              )}
              <p className="text-sm text-gray-500">
                {formatBytes(selectedFile.size)} • {selectedFile.type}
              </p>
              {selectedFile.size > chunkThreshold && (
                <p className="text-xs text-blue-600">
                  Using chunked upload for optimal reliability
                </p>
              )}
              {getLargeFileWarning(selectedFile) && (
                <p className="text-xs text-amber-600 mt-1">
                  {getLargeFileWarning(selectedFile)}
                </p>
              )}
            </div>
            <div className="flex items-center space-x-2 ml-4">
              {isUploading && !isPaused && (
                <button
                  onClick={pauseUpload}
                  className="p-2 text-gray-600 hover:text-gray-800"
                  title="Pause upload"
                >
                  <Pause className="w-5 h-5" />
                </button>
              )}
              {isUploading && isPaused && (
                <button
                  onClick={resumeUpload}
                  className="p-2 text-blue-600 hover:text-blue-800"
                  title="Resume upload"
                >
                  <Play className="w-5 h-5" />
                </button>
              )}
              <button
                onClick={cancelUpload}
                className="p-2 text-red-600 hover:text-red-800"
                title="Cancel upload"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-gray-600">
              <span>
                {isPaused ? 'Paused' : isCompleted ? 'Completed' : 'Uploading...'}
              </span>
              <span>{progress.percentage.toFixed(1)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all duration-300 ${
                  isCompleted ? 'bg-green-600' : 
                  isPaused ? 'bg-yellow-500' : 'bg-blue-600'
                }`}
                style={{ width: `${progress.percentage}%` }}
              />
            </div>
            <div className="flex justify-between text-xs text-gray-500">
              <span>
                {formatBytes(progress.uploadedBytes)} / {formatBytes(progress.totalBytes)}
              </span>
              <span>
                {progress.speed > 0 && `${formatBytes(progress.speed)}/s`}
                {progress.timeRemaining > 0 && ` • ${formatTime(progress.timeRemaining)} remaining`}
              </span>
            </div>
            {selectedFile.size > chunkThreshold && (
              <div className="flex justify-between text-xs text-gray-500">
                <span>
                  Chunk {progress.currentChunk} of {progress.totalChunks}
                </span>
                <span>
                  {progress.currentChunk} / {progress.totalChunks} chunks uploaded
                </span>
              </div>
            )}
          </div>

          {isCompleted && (
            <div className="flex items-center space-x-2 text-green-600">
              <CheckCircle className="w-5 h-5" />
              <span className="font-medium">Upload completed successfully!</span>
            </div>
          )}
        </div>
      )}

      {/* Error State */}
      {hasError && error && (
        <div className="space-y-4 p-6 bg-white rounded-lg border border-red-200">
          <div className="flex items-center space-x-2 text-red-600">
            <AlertCircle className="w-5 h-5" />
            <span className="font-medium">Upload failed</span>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-800">{error}</p>
            {(error.includes('Authentication') || error.includes('log in')) && (
              <div className="mt-3 p-2 bg-red-100 rounded border">
                <p className="text-xs text-red-600">
                  💡 <strong>Solution:</strong> Please refresh the page and log in again, then try uploading.
                </p>
              </div>
            )}
          </div>
          <button
            onClick={cancelUpload}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      )}
    </div>
  );
};

export default SmartFileUpload;